import { useCallback, useEffect, useState } from "react";
import { useDashboardModules } from "@/hooks/useDashboardModules";
import {
  DashboardNavList,
  DashboardNavMenuButton,
  DashboardSidebar,
} from "@/components/dashboard/DashboardSidebar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import {
  readNavSidebarCollapsed,
  writeNavSidebarCollapsed,
} from "@/lib/sidebarNavStorage";

interface StandardPageShellProps {
  readonly children: React.ReactNode;
  readonly title: string;
  readonly className?: string;
  readonly headerRight?: React.ReactNode;
}

export function StandardPageShell({
  children,
  title,
  className,
  headerRight,
}: StandardPageShellProps) {
  const { user } = useAuth();
  const dashboardNavModules = useDashboardModules();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    setSidebarCollapsed(readNavSidebarCollapsed(user?.id));
  }, [user?.id]);

  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      writeNavSidebarCollapsed(user?.id, next);
      return next;
    });
  }, [user?.id]);

  return (
    <div className="min-h-[100dvh] bg-background">
      <div className="flex min-h-[100dvh] flex-col lg:flex-row">
        <div className="hidden lg:sticky lg:top-0 lg:flex lg:h-[100dvh] lg:min-h-[100dvh] lg:shrink-0">
          <DashboardSidebar
            modules={dashboardNavModules}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={handleToggleSidebar}
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="border-b border-border bg-background/95 px-3 py-2.5 sm:px-6 sm:py-4 lg:px-8">
            <div className="mx-auto flex max-w-7xl min-w-0 items-center justify-between gap-2 sm:gap-3">
              <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                <DashboardNavMenuButton onOpen={() => setMobileNavOpen(true)} />
                {title ? (
                  <h1 className="font-ui min-w-0 truncate text-lg font-semibold text-foreground sm:text-2xl">
                    {title}
                  </h1>
                ) : null}
              </div>
              {headerRight && (
                <div className="flex shrink-0 items-center gap-1 sm:gap-2">
                  {headerRight}
                </div>
              )}
            </div>
          </div>

          <main
            className={
              className ??
              "mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-6 sm:py-6 lg:px-8"
            }
          >
            {children}
          </main>
        </div>
      </div>

      <Dialog open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <DialogContent className="flex max-h-[85dvh] max-w-md flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
          <DialogHeader className="shrink-0 border-b border-border px-6 pb-4 pt-6">
            <DialogTitle className="font-ui">Navigation</DialogTitle>
          </DialogHeader>
          <DashboardNavList
            modules={dashboardNavModules}
            onNavigate={() => setMobileNavOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
